import { useCallback, useEffect, useState } from "react";
import { AxiosInstance } from "../services/Axios";

const SCRAPING_PROXY_BASE =
  process.env.REACT_APP_SCRAPING_PROXY_URL ??
  process.env.REACT_APP_BACKEND_URL;

const scrapingProxyConfig = { baseURL: SCRAPING_PROXY_BASE };
const isLocalScrapingHost = Boolean(process.env.REACT_APP_SCRAPING_PROXY_URL);

const scrapingPath = (relativePath) =>
  isLocalScrapingHost ? relativePath : `scraping-proxy/${relativePath}`;

function normalizeError(err, fallback = "Unexpected error") {
  const data = err?.response?.data;
  const raw = data?.error ?? data?.detail ?? err?.message ?? fallback;
  if (Array.isArray(raw)) {
    return raw
      .map((e) => {
        if (typeof e === "string") return e;
        const loc = Array.isArray(e?.loc) ? e.loc.join(".") : "";
        return loc ? `${loc}: ${e?.msg ?? "invalid"}` : e?.msg ?? JSON.stringify(e);
      })
      .join("; ");
  }
  return typeof raw === "string" ? raw : JSON.stringify(raw);
}

export function useTwitterConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await AxiosInstance.get(
        scrapingPath("twitter/config"),
        scrapingProxyConfig,
      );
      setConfig(res.data || null);
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const saveConfig = useCallback(async (patch) => {
    try {
      setSaving(true);
      setError(null);
      const res = await AxiosInstance.put(
        scrapingPath("twitter/config"),
        patch,
        scrapingProxyConfig,
      );
      setConfig(res.data);
      return res.data;
    } catch (err) {
      const msg = normalizeError(err);
      setError(msg);
      throw new Error(msg);
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    config,
    loading,
    saving,
    error,
    setError,
    clearError: () => setError(null),
    saveConfig,
    refetch: fetchConfig,
  };
}

export function useTwitterAccounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await AxiosInstance.get(
        scrapingPath("twitter/accounts"),
        scrapingProxyConfig,
      );
      setRows(res.data?.data || []);
    } catch (err) {
      setError(normalizeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const createAccount = useCallback(async (payload) => {
    try {
      const res = await AxiosInstance.post(
        scrapingPath("twitter/accounts"),
        payload,
        scrapingProxyConfig,
      );
      const created = res.data;
      setRows((prev) => [created, ...prev]);
      return created;
    } catch (err) {
      throw new Error(normalizeError(err));
    }
  }, []);

  const updateAccount = useCallback(async (username, patch) => {
    try {
      const res = await AxiosInstance.put(
        scrapingPath(`twitter/accounts/${encodeURIComponent(username)}`),
        patch,
        scrapingProxyConfig,
      );
      const updated = res.data;
      setRows((prev) =>
        prev.map((r) => (r.username === username ? updated : r)),
      );
      return updated;
    } catch (err) {
      throw new Error(normalizeError(err));
    }
  }, []);

  const deleteAccount = useCallback(async (username) => {
    try {
      await AxiosInstance.delete(
        scrapingPath(`twitter/accounts/${encodeURIComponent(username)}`),
        scrapingProxyConfig,
      );
      setRows((prev) => prev.filter((r) => r.username !== username));
    } catch (err) {
      throw new Error(normalizeError(err));
    }
  }, []);

  const toggleActive = useCallback(async (username, active) => {
    try {
      const res = await AxiosInstance.patch(
        scrapingPath(`twitter/accounts/${encodeURIComponent(username)}/active`),
        { active },
        scrapingProxyConfig,
      );
      const updated = res.data;
      setRows((prev) =>
        prev.map((r) => (r.username === username ? updated : r)),
      );
      return updated;
    } catch (err) {
      throw new Error(normalizeError(err));
    }
  }, []);

  return {
    rows,
    loading,
    error,
    setError,
    clearError: () => setError(null),
    createAccount,
    updateAccount,
    deleteAccount,
    toggleActive,
    refetch: fetchAccounts,
  };
}
